// client/src/pages/LostFoundDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function LostFoundDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimMsg, setClaimMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  /* LOAD ITEM */
  const load = async () => {
    try {
      const res = await fetch(`${API}/lost-found/${id}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Failed");
      setItem(d.item);
    } catch (err) {
      alert(err.message || "Error");
      navigate("/lost-found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div className="p-6">Loading…</div>;
  if (!item) return <div className="p-6">Not found</div>;

  /* SAFE USER CHECKS */
  const currentUserId = user?.id ? String(user.id) : null;
  const isOwner = currentUserId && String(currentUserId) === String(item.postedBy);

  const confirmedClaim = item.confirmedClaim
    ? item.claims?.find((c) => String(c._id) === String(item.confirmedClaim))
    : null;

  const isConfirmedClaimant =
    confirmedClaim && currentUserId && String(confirmedClaim.user) === String(currentUserId);

  /* SHOW CLAIM BOX: user is not owner + item is open + user has not claimed */
  const showClaimBox =
    !isOwner &&
    !["success", "expired"].includes(item.status) &&   // allow claiming in ALL stages except final ones
    !item.claims.some((c) => String(c.user) === String(currentUserId || ""));

  /* SUBMIT CLAIM */
  const submitClaim = async () => {
    if (!user) return navigate("/login");
    if (!claimMsg.trim()) return alert("Add a message");

    setSending(true);
    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/lost-found/${id}/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: claimMsg }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      alert("Claim submitted");
      setClaimMsg("");
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  /* OWNER CONFIRMS CLAIM */
  const ownerConfirmClaim = async (claimId) => {
    if (!isOwner) return;
    if (!window.confirm("Confirm this claimant?")) return;

    setConfirmLoading(true);
    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/lost-found/${id}/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ claimId }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setConfirmLoading(false);
    }
  };

  /* OWNER UNCONFIRM CLAIMANT */
  const ownerUnconfirm = async () => {
    if (!isOwner || !confirmedClaim) return;

    if (!window.confirm("Remove confirmed claimant?")) return;

    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/lost-found/${id}/unconfirm`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      alert("Confirmed claimant removed.");
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  /* OWNER MARK DELIVERED */
  const ownerMarkDelivered = async () => {
    if (!isOwner || !confirmedClaim) return;

    if (!window.confirm("Mark item as delivered?")) return;

    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/lost-found/${id}/delivered`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      load();
    } catch (err) {
      alert(err.message);
    }
  };

  /* CLAIMANT CONFIRM RECEIVED */
  const claimantConfirmReceived = async (yes) => {
    if (!isConfirmedClaimant) return;
    if (!window.confirm("Are you sure?")) return;

    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/lost-found/${id}/confirm-received`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ yes }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      if (d.removed) {
        alert("Confirmed — item removed.");
        return navigate("/lost-found");
      }

      if (d.message?.toLowerCase().includes("denied")) {
        alert("You said NO — poster notified.");
        return load();
      }

      load();
    } catch (err) {
      alert(err.message);
    }
  };

  /* CHECK IF POSTER HAS MESSAGED A CLAIMANT BEFORE (for chat visibility) */
  const posterMessagedThisClaimant = (uid) => {
    return (
      Array.isArray(item.postersMessaged) &&
      item.postersMessaged.some((pm) => String(pm) === String(uid))
    );
  };

  return (
    <div className="min-h-screen p-6 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 p-6 rounded-xl shadow">

        {/* HEADER */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold dark:text-white">{item.title}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
              {item.location}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-1 border rounded dark:text-slate-200"
          >
            Back
          </button>
        </div>

        {/* IMAGE */}
        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt="item"
            className="w-full h-64 object-cover rounded-xl mt-6"
          />
        )}

        {/* DESCRIPTION */}
        <p className="mt-4 text-slate-700 dark:text-slate-200 whitespace-pre-line">
          {item.description}
        </p>

        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
          Posted by: {item.postedByName}
        </p>

        {/* POSTER STATUS MESSAGE */}
        {isOwner && item.status === "delivered_pending" && (
          <div className="mt-4 p-3 bg-blue-200 dark:bg-blue-900 rounded">
            Waiting for claimant to confirm they received the item…
          </div>
        )}

        {/* CLAIMS */}
        <div className="mt-6">
          <h3 className="font-semibold dark:text-white">Claims</h3>

          {item.claims?.length === 0 && (
            <p className="text-slate-500 mt-2">No claims yet.</p>
          )}

          <div className="space-y-3 mt-3 ">
            {item.claims?.map((c) => {
              const isConfirmed =
                confirmedClaim &&
                String(c._id) === String(confirmedClaim?._id);

              const thisClaimantIsCurrentUser =
                currentUserId &&
                String(c.user) === String(currentUserId);

              const showChatToClaimant =
                thisClaimantIsCurrentUser &&
                (isConfirmed || posterMessagedThisClaimant(c.user));

              return (
                <div
                  key={c._id}
                  className={`p-3 rounded flex justify-between items-start ${
                    isConfirmed
                      ? "bg-blue-100 dark:bg-blue-950 border border-blue-500"
                      : "bg-slate-100 dark:bg-slate-900"
                  }`}
                >
                  <div>
                    <div className="font-medium dark:text-white">
                      {c.userName}
                    </div>
                    <div className="text-sm text-slate-500 ">{c.message}</div>
                    <div className="text-sm text-slate-400 mt-1 ">
                      {new Date(c.createdAt).toLocaleString()}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">

                    {/* OWNER CONFIRM */}
                    {isOwner &&
                      item.status !== "success" &&
                      !confirmedClaim &&
                      item.status === "open" && (
                        <button
                          onClick={() => ownerConfirmClaim(c._id)}
                          className="px-3 py-1 bg-green-600 text-white rounded"
                        >
                          {confirmLoading ? "..." : "Confirm"}
                        </button>
                      )}

                    {/* OWNER UNCONFIRM */}
                    {isOwner &&
                      isConfirmed &&
                      item.status !== "success" && (
                        <button
                          onClick={ownerUnconfirm}
                          className="px-3 py-1 bg-red-600 text-white rounded"
                        >
                          Remove
                        </button>
                      )}

                    {/* POSTER ALWAYS CAN CHAT */}
                    {isOwner && (
                      <button
                        onClick={() =>
                          navigate(`/chat/lost/${item._id}/${c.user}`)
                        }
                        className="px-3 py-1 border rounded"
                      >
                        Chat
                      </button>
                    )}

                    {/* CLAIMANT CHAT BUTTON */}
                    {showChatToClaimant && (
                      <button
                        onClick={() =>
                          navigate(`/chat/lost/${item._id}/${c.user}`)
                        }
                        className="px-3 py-1 border rounded"
                      >
                        Chat
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CLAIM BOX */}
        {showClaimBox && (
          <div className="mt-6 dark:text-slate-200">
            <h3 className="font-semibold dark:text-white">Claim this item</h3>

            <textarea
              value={claimMsg}
              onChange={(e) => setClaimMsg(e.target.value)}
              rows={3}
              className="w-full p-2 mt-2 rounded border dark:bg-slate-700"
              placeholder="Explain why this is yours"
            ></textarea>

            <div className="flex gap-3 mt-3 ">
              <button
                onClick={submitClaim}
                disabled={sending}
                className="px-4 py-2 bg-indigo-600 text-white rounded"
              >
                {sending ? "Sending…" : "Submit Claim"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 border rounded dark:text-slate-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* CLAIMANT YES / NO */}
        {isConfirmedClaimant && item.status === "delivered_pending" && (
          <div className="mt-6 p-4 rounded bg-yellow-100 dark:bg-yellow-900">
            <p className="font-medium dark:text-white">
              Did you receive the item?
            </p>

            <div className="flex gap-3 mt-3">
              <button
                onClick={() => claimantConfirmReceived(true)}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                YES
              </button>

              <button
                onClick={() => claimantConfirmReceived(false)}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                NO
              </button>
            </div>
          </div>
        )}

        {/* POSTER MARK DELIVERED */}
        {isOwner &&
          confirmedClaim &&
          item.status === "awaiting_claimer_confirmation" && (
            <button
              onClick={ownerMarkDelivered}
              className="mt-6 px-4 py-2 bg-blue-600 text-white rounded"
            >
              Mark Delivered
            </button>
          )}

        {/* SUCCESS MESSAGE */}
        {item.status === "success" && (
          <div className="mt-6 p-3 bg-green-50 dark:bg-green-900 text-green-700 dark:text-green-200 rounded">
            This item has been successfully returned 🎉
          </div>
        )}
      </div>
    </div>
  );
}
